import React, { useState } from "react";
import { Lock, Unlock, Eye, EyeOff, Check, AlertCircle, Copy, Share2, X } from "lucide-react";
import { Structure, api } from "../lib/api";
import { ConfirmModal } from "./ConfirmModal";

type PasswordSettingsProps = {
  structure: Structure;
  onUpdate: (updated: Structure) => void;
};

export const PasswordSettings: React.FC<PasswordSettingsProps> = ({ structure, onUpdate }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  const handleSetPassword = async () => {
    setError(null);
    setSuccess(null);

    if (password.length < 4) {
      setError("Passwort muss mindestens 4 Zeichen lang sein");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwörter stimmen nicht überein");
      return;
    }

    setLoading(true);
    try {
      await api.setPassword(structure.id, password);
      const updated = await api.getStructure(structure.id);
      onUpdate(updated);
      setSuccess("Passwort erfolgreich gesetzt");
      setPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(err.message || "Fehler beim Setzen des Passworts");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePassword = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await api.removePassword(structure.id);
      const updated = await api.getStructure(structure.id);
      onUpdate(updated);
      setSuccess("Passwort-Schutz entfernt");
    } catch (err: any) {
      setError(err.message || "Fehler beim Entfernen des Passworts");
    } finally {
      setLoading(false);
    }
  };

  const requestRemovePassword = () => {
    setConfirmRemoveOpen(true);
  };

  const confirmRemovePassword = async () => {
    setConfirmRemoveOpen(false);
    await handleRemovePassword();
  };

  const handleToggleShare = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (structure.share_enabled) {
        const response = await api.disableShare(structure.id);
        const updated = await api.getStructure(structure.id);
        onUpdate(updated);
        setSuccess("Teilen deaktiviert - Struktur ist jetzt privat");
        if (response.generated_password) {
          setGeneratedPassword(response.generated_password);
        }
      } else {
        const response = await api.enableShare(structure.id);
        const updated = await api.getStructure(structure.id);
        onUpdate({ ...updated, share_token: response.share_token });
        setSuccess("Teilen aktiviert");
      }
    } catch (err: any) {
      setError(err.message || "Fehler beim Ändern der Freigabe");
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = () => {
    if (!structure.share_token) return;
    const url = `${window.location.origin}/share/${structure.share_token}`;
    navigator.clipboard.writeText(url);
    setSuccess("Link kopiert!");
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Zugriffskontrolle</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Verwalte den Zugriff auf diese Struktur
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md flex items-start gap-2">
          <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-300">{success}</p>
        </div>
      )}

      {/* Share Toggle */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Share2 className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              <h4 className="font-medium text-slate-900 dark:text-white">Öffentlicher Zugriff</h4>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {structure.share_enabled
                ? "Jeder mit dem Link kann die Struktur ansehen"
                : "Struktur ist privat - nur mit Passwort zugänglich"}
            </p>
          </div>
          <button
            onClick={handleToggleShare}
            disabled={loading}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              structure.share_enabled
                ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50"
                : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50"
            }`}
          >
            {structure.share_enabled ? "Deaktivieren" : "Aktivieren"}
          </button>
        </div>

        {structure.share_enabled && structure.share_token && (
          <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm text-slate-700 dark:text-slate-300 truncate">
                {window.location.origin}/share/{structure.share_token}
              </code>
              <button
                onClick={copyShareLink}
                className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
                title="Link kopieren"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Password Protection */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          <h4 className="font-medium text-slate-900 dark:text-white">Bearbeitungs-Passwort</h4>
        </div>

        {structure.edit_password_enabled ? (
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Passwort-Schutz ist aktiv. Zum Bearbeiten ist ein Passwort erforderlich.
              </p>
            </div>
            <button
              onClick={requestRemovePassword}
              disabled={loading || !structure.share_enabled}
              className="btn btn-secondary w-full"
              title={!structure.share_enabled ? "Aktiviere zuerst öffentlichen Zugriff" : ""}
            >
              <Unlock className="h-4 w-4" />
              Passwort-Schutz entfernen
            </button>
            {!structure.share_enabled && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Hinweis: Passwort kann nur entfernt werden, wenn die Struktur öffentlich ist
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Neues Passwort (min. 4 Zeichen)
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  placeholder="Passwort eingeben..."
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 dark:hover:bg-slate-600 rounded"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Passwort bestätigen
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                placeholder="Passwort wiederholen..."
                disabled={loading}
              />
            </div>

            <button
              onClick={handleSetPassword}
              disabled={loading || !password || !confirmPassword}
              className="btn btn-primary w-full"
            >
              <Lock className="h-4 w-4" />
              Passwort setzen
            </button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
          Zugriffs-Szenarien:
        </h5>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li>
            • <strong>Öffentlich + Kein Passwort:</strong> Jeder kann ansehen und bearbeiten
          </li>
          <li>
            • <strong>Öffentlich + Passwort:</strong> Ansehen frei, Bearbeiten nur mit Passwort
          </li>
          <li>
            • <strong>Privat + Passwort:</strong> Ansehen und Bearbeiten nur mit Passwort
          </li>
        </ul>
      </div>

      <ConfirmModal
        isOpen={confirmRemoveOpen}
        title="Passwort-Schutz entfernen?"
        description="Damit kann jeder mit dem Link bearbeiten."
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        tone="danger"
        confirmDisabled={loading}
        onConfirm={confirmRemovePassword}
        onCancel={() => setConfirmRemoveOpen(false)}
      />

      {generatedPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Privat geschaltet</p>
                <h3 className="text-lg font-semibold">Passwort gespeichert</h3>
              </div>
              <button
                onClick={() => setGeneratedPassword(null)}
                className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Die Struktur ist jetzt privat. Dieses automatisch gesetzte Passwort wird nur einmal angezeigt – bitte sicher
                ablegen.
              </p>
              <div className="flex items-center gap-3">
                <code className="flex-1 px-3 py-2 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-mono">
                  {generatedPassword}
                </code>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigator.clipboard.writeText(generatedPassword)}
                >
                  Kopieren
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
              <button className="btn btn-primary" onClick={() => setGeneratedPassword(null)}>
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
